import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HarmonyShoppingCart } from "@/components/icons";
import { useCart } from "@/features/cart";
import { CartDropdown } from "@/features/cart/components/CartDropdown";
import { cn } from "@/lib/utils";
import { headerActionClass } from "@/components/layout/header-action";

export const HeaderCartIndicator: React.FC = () => {
  const { t } = useTranslation();
  const { totalItems, isLoading, isEmpty } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [isWaveActive, setIsWaveActive] = useState(false);
  const prevTotalItemsRef = useRef<number | null>(null);
  const waveTimeoutRef = useRef<number | null>(null);

  const cartCount = totalItems > 99 ? "99+" : String(totalItems);
  const buttonAriaLabel = `${t("cart.title")} (${totalItems})`;

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const prevTotal = prevTotalItemsRef.current;

    // İlk yüklemede veya loading sürecinde wave tetikleme
    if (prevTotal === null || isLoading) {
      prevTotalItemsRef.current = totalItems;
      return;
    }

    // Sadece sepetteki ürün adedi arttığında wave efekti çalışsın
    if (totalItems > prevTotal) {
      setIsWaveActive(true);

      if (waveTimeoutRef.current) {
        window.clearTimeout(waveTimeoutRef.current);
      }

      waveTimeoutRef.current = window.setTimeout(() => {
        setIsWaveActive(false);
        waveTimeoutRef.current = null;
      }, 720);
    }

    prevTotalItemsRef.current = totalItems;
  }, [totalItems, isLoading]);

  useEffect(() => {
    return () => {
      if (waveTimeoutRef.current) {
        window.clearTimeout(waveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={cn(
        "relative h-8 shrink-0 overflow-visible transition-[width] duration-300 ease-out",
        isOpen ? "w-[124px]" : "w-8",
      )}
    >
      <style>
        {`
          @keyframes cart-icon-wave {
            0% { transform: scale(1); opacity: 0.72; }
            72% { transform: scale(3.1); opacity: 0.2; }
            100% { transform: scale(3.9); opacity: 0; }
          }
          @keyframes cart-icon-pop {
            0% { transform: scale(1); }
            35% { transform: scale(1.16); }
            100% { transform: scale(1); }
          }
        `}
      </style>

      <button
        type="button"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={handleToggle}
        aria-label={buttonAriaLabel}
        className={cn(
          headerActionClass,
          "w-full overflow-visible",
          isOpen ? "justify-start px-2" : "px-1",
        )}
      >
        <span className="flex h-full w-full min-w-0 flex-nowrap items-center gap-2 whitespace-nowrap">
          <span className="relative flex size-5 shrink-0 items-center justify-center">
            {isWaveActive && (
              <>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute size-5 rounded-full border border-border-secondary dark:border-white/20"
                  style={{ animation: "cart-icon-wave 760ms ease-out forwards" }}
                />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute size-5 rounded-full border border-border-secondary/70 dark:border-white/15"
                  style={{ animation: "cart-icon-wave 880ms ease-out forwards" }}
                />
              </>
            )}
            <span
              className="inline-flex"
              style={
                isWaveActive
                  ? { animation: "cart-icon-pop 280ms ease-out" }
                  : undefined
              }
            >
              <HarmonyShoppingCart className="size-4" />
            </span>
            {!isEmpty ? (
              <span className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-text-primary px-0.5 text-[8px] font-bold text-white dark:bg-white dark:text-gray-900">
                {cartCount}
              </span>
            ) : (
              isLoading && (
                <span className="absolute -right-0.5 -top-0.5 size-2 animate-pulse rounded-full bg-gray-300 dark:bg-gray-600" />
              )
            )}
          </span>

          <span
            className={cn(
              "overflow-hidden whitespace-nowrap text-[11px] font-semibold leading-none transition-[max-width,opacity] duration-300",
              isOpen ? "max-w-[80px] opacity-100" : "max-w-0 opacity-0",
            )}
          >
            {t("cart.title")}
          </span>
        </span>
      </button>

      <CartDropdown isOpen={isOpen} onClose={handleClose} />
    </div>
  );
};
